import React, { useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  publicFolderUploadApi,
  FolderUploadSessionDto,
  FolderDocumentRequestItemDto,
} from '../services/folderDocumentService';

// ============================================================================
// STYLED COMPONENTS - Professional formal design
// ============================================================================

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
  padding: 40px 20px;
  direction: rtl;
`;

const MainCard = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

/* Previous blue gradient design (saved for reference):
const Header = styled.div`
  background: linear-gradient(135deg, #012A64 0%, #0055b3 100%);
  color: white;
  padding: 24px 32px;
  border-radius: 8px 8px 0 0;
  text-align: center;
`;
*/

const Header = styled.div`
  background: white;
  padding: 32px;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const UploadLogo = styled.img`
  height: 70px;
  object-fit: contain;
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-color);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

const Content = styled.div`
  padding: 32px;
`;

// Step Indicator
const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
  gap: 40px;
`;

const Step = styled.div<{ $active?: boolean; $completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 20px;
    left: -30px;
    width: 20px;
    height: 2px;
    background: ${props => props.$completed ? '#10b981' : '#e5e7eb'};
  }
`;

const StepCircle = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  background: ${props => {
    if (props.$completed || props.$active) return '#10b981';
    return '#e5e7eb';
  }};
  color: ${props => (props.$completed || props.$active) ? 'white' : '#9ca3af'};
`;

const StepLabel = styled.span<{ $active?: boolean; $completed?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => {
    if (props.$active || props.$completed) return '#10b981';
    return '#6b7280';
  }};
`;

const Section = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const InfoBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
`;

const ProgressContainer = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number; isComplete?: boolean }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: ${props => props.isComplete ? '#9ca3af' : '#012A64'};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
`;

const ErrorAlert = styled.div`
  background: #fef2f2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  border: 1px solid #fecaca;
  font-size: 14px;
`;

// ============================================================================
// UPLOAD CARDS GRID - Windows Explorer style cubes
// ============================================================================

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const UploadCard = styled.div<{ isUploaded: boolean; isUploading: boolean; isDragOver: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  padding: 20px;
  background: ${props => {
    if (props.isUploaded) return '#fafafa';
    if (props.isDragOver) return '#eff6ff';
    return '#fafafa';
  }};
  border: 2px ${props => props.isUploaded ? 'solid' : 'dashed'} ${props => {
    if (props.isUploaded) return '#d1d5db';
    if (props.isDragOver) return '#012A64';
    return '#d1d5db';
  }};
  border-radius: 8px;
  cursor: ${props => props.isUploading ? 'wait' : 'pointer'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.isUploaded ? '#9ca3af' : '#012A64'};
    background: ${props => props.isUploaded ? '#f5f5f5' : '#f8fafc'};
  }
`;

const CardIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  margin-bottom: 4px;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
`;

const MandatoryBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  margin-top: 8px;
`;

const OptionalBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  margin-top: 8px;
`;

// Uploaded file display - similar to system view
const UploadedFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

// Clickable area for preview (icon + filename)
const PreviewClickArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const FileName = styled.div`
  font-size: 12px;
  color: #374151;
  text-align: center;
  word-break: break-word;
  margin-top: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ReplaceText = styled.span`
  font-size: 11px;
  color: #3b82f6;
  margin-top: 8px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

// ============================================================================
// ACCESS CODE FORM
// ============================================================================

const AccessForm = styled.div`
  max-width: 400px;
  margin: 0 auto;
  text-align: center;
`;

const AccessInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  font-size: 18px;
  text-align: center;
  letter-spacing: 3px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : '#d1d5db'};
  border-radius: 6px;
  margin: 24px 0;
  font-weight: 600;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(220, 38, 38, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'success'; fullWidth?: boolean }>`
  padding: 12px 24px;
  border: 1px solid ${props => props.variant === 'success' ? '#059669' : 'var(--primary-color)'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  ${props => props.fullWidth && 'width: 100%;'}

  background: ${props => props.variant === 'success' ? '#059669' : 'var(--primary-color)'};
  color: white;

  &:hover:not(:disabled) {
    background: ${props => props.variant === 'success' ? '#047857' : 'transparent'};
    color: ${props => props.variant === 'success' ? 'white' : 'var(--primary-color)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// COMPLETION SECTION
// ============================================================================

const CompletionBox = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  margin-top: 24px;
`;

const CompletionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const CompletionText = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #6b7280;
`;

const CompletedView = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const CompletedIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const CompletedTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #374151;
`;

const CompletedText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6b7280;
`;

// ============================================================================
// EXPIRED VIEW
// ============================================================================

const ExpiredView = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const ExpiredIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const ExpiredTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 600;
  color: #dc2626;
`;

const ExpiredText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #6b7280;
  line-height: 1.7;
`;

// ============================================================================
// PREVIEW MODAL
// ============================================================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
  min-height: 400px;
  background: #ffffff;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: 500px;
  border: none;
  background: white;
  border-radius: 8px;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const UnsupportedPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px;
`;

const UnsupportedTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
`;

const UnsupportedText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: #4a4a4a;
`;

const PreviewLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #6b7280;
  font-size: 15px;
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: transparent;
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const getFileIcon = (fileName: string): React.ReactNode => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  // PDF - Red
  if (ext === 'pdf') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#fef2f2"/>
        <path d="M14 2V8H20" stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#dc2626">PDF</text>
      </svg>
    );
  }

  // Word - Blue
  if (ext === 'doc' || ext === 'docx') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#eff6ff"/>
        <path d="M14 2V8H20" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#2563eb">DOC</text>
      </svg>
    );
  }

  // Excel - Green
  if (ext === 'xls' || ext === 'xlsx') {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#f0fdf4"/>
        <path d="M14 2V8H20" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <text x="12" y="16" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#16a34a">XLS</text>
      </svg>
    );
  }

  // Images - Purple
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="#7c3aed" strokeWidth="1.5" fill="#faf5ff"/>
        <circle cx="8.5" cy="8.5" r="1.5" fill="#7c3aed"/>
        <path d="M21 15L16 10L5 21" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }

  // Default - Gray document
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="#f9fafb"/>
      <path d="M14 2V8H20" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 13H16M8 17H12" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
};

const validateFileSize = (file: File): string | null => {
  if (file.size > MAX_FILE_SIZE) {
    return `גודל הקובץ חורג מהמותר (מקסימום 2MB)`;
  }
  return null;
};

const isPreviewableFile = (extension: string): { canPreview: boolean; type: 'pdf' | 'image' | 'unsupported' } => {
  const ext = extension.toLowerCase().replace('.', '');

  if (ext === 'pdf') {
    return { canPreview: true, type: 'pdf' };
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return { canPreview: true, type: 'image' };
  }

  return { canPreview: false, type: 'unsupported' };
};

const getFileTypeName = (extension: string): string => {
  const ext = extension.toLowerCase().replace('.', '');
  switch (ext) {
    case 'doc':
    case 'docx':
      return 'Word';
    case 'xls':
    case 'xlsx':
      return 'Excel';
    case 'ppt':
    case 'pptx':
      return 'PowerPoint';
    default:
      return ext.toUpperCase();
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PublicFolderUploadPage: React.FC = () => {
  const { publicId } = useParams<{ publicId: string }>();
  const [step, setStep] = useState<'access' | 'upload' | 'completed' | 'expired'>('access');
  const [accessCode, setAccessCode] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [session, setSession] = useState<FolderUploadSessionDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingItemId, setUploadingItemId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadedItems, setUploadedItems] = useState<Set<number>>(new Set());
  const [uploadedFileNames, setUploadedFileNames] = useState<Record<number, string>>({});
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<Record<number, number>>({});
  const [dragOverItemId, setDragOverItemId] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // Preview modal state
  const [previewItem, setPreviewItem] = useState<FolderDocumentRequestItemDto | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Progress calculations
  const progressStats = useMemo(() => {
    if (!session) {
      return { total: 0, uploaded: 0, mandatoryTotal: 0, mandatoryUploaded: 0 };
    }

    const mandatoryItems = session.requestItems.filter(item => item.isMandatory);
    const uploaded = session.requestItems.filter(item => item.isReceived || uploadedItems.has(item.id));
    const mandatoryUploaded = mandatoryItems.filter(item => item.isReceived || uploadedItems.has(item.id));

    return {
      total: session.requestItems.length,
      uploaded: uploaded.length,
      mandatoryTotal: mandatoryItems.length,
      mandatoryUploaded: mandatoryUploaded.length,
    };
  }, [session, uploadedItems]);

  const progressPercent = progressStats.total > 0
    ? Math.round((progressStats.uploaded / progressStats.total) * 100)
    : 0;

  const allMandatoryUploaded = progressStats.mandatoryUploaded === progressStats.mandatoryTotal && progressStats.mandatoryTotal > 0;

  // Access code submission
  const handleAccessCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!publicId || accessCode.length < 4) {
      setError('קוד גישה לא תקין');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await publicFolderUploadApi.verifyAccessCode(publicId, accessCode);
      setAccessToken(response.token);

      const sessionData = await publicFolderUploadApi.getUploadSession(publicId, response.token);
      setSession(sessionData);
      setStep('upload');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || '';
      const isExpired = errorMessage.includes('פג תוקף') || err.response?.status === 410;

      if (isExpired) {
        setStep('expired');
      } else if (err.response?.status === 401) {
        setError('קוד גישה שגוי');
      } else if (err.response?.status === 404) {
        setError('בקשת מסמכים לא נמצאה');
      } else {
        setError(errorMessage || 'שגיאה באימות קוד הגישה');
      }
    } finally {
      setLoading(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (item: FolderDocumentRequestItemDto, file: File) => {
    if (!publicId || !session) return;

    // Validate file size
    const sizeError = validateFileSize(file);
    if (sizeError) {
      setError(sizeError);
      return;
    }

    setUploadingItemId(item.id);
    setError(null);

    try {
      const result = await publicFolderUploadApi.uploadDocument(
        publicId,
        accessToken,
        file,
        session.clientId,
        item.targetFolderId,
        undefined, // notes
        item.id // fulfilledRequestItemId
      );

      setUploadedItems(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.add(item.id);
        return newSet;
      });
      setUploadedFileNames(prev => ({ ...prev, [item.id]: file.name }));
      setUploadedDocumentIds(prev => ({ ...prev, [item.id]: result.documentId }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'שגיאה בהעלאת הקובץ');
    } finally {
      setUploadingItemId(null);
    }
  };

  // Preview handlers
  const handlePreviewClick = async (item: FolderDocumentRequestItemDto, e: React.MouseEvent) => {
    e.stopPropagation();

    // Get the document ID - either from current session upload or from backend
    const documentId = uploadedDocumentIds[item.id] || item.uploadedDocumentId;
    const fileName = uploadedFileNames[item.id] || item.uploadedFileName;
    const fileExtension = fileName ? '.' + fileName.split('.').pop() : item.uploadedFileExtension;

    if (!documentId || !publicId || !fileExtension) return;

    setPreviewItem({ ...item, uploadedFileName: fileName, uploadedFileExtension: fileExtension });
    setPreviewUrl(null);

    const previewInfo = isPreviewableFile(fileExtension);

    if (previewInfo.canPreview) {
      setIsLoadingPreview(true);
      try {
        const url = await publicFolderUploadApi.getDocumentPreviewUrl(publicId, accessToken, documentId);
        setPreviewUrl(url);
      } catch (error) {
        console.error('Error loading preview:', error);
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const handleClosePreview = () => {
    setPreviewItem(null);
    setPreviewUrl(null);
  };

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      handleClosePreview();
    }
    mouseDownTargetRef.current = null;
  };

  // Download handler for non-previewable files
  const handleDownload = async () => {
    if (!previewItem || !publicId) return;

    const documentId = uploadedDocumentIds[previewItem.id] || previewItem.uploadedDocumentId;
    if (!documentId) return;

    setIsDownloading(true);
    try {
      const url = await publicFolderUploadApi.getDocumentPreviewUrl(publicId, accessToken, documentId);
      // Create a temporary link and click it to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = previewItem.uploadedFileName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, itemId: number) => {
    e.preventDefault();
    setDragOverItemId(itemId);
  };

  const handleDragLeave = () => {
    setDragOverItemId(null);
  };

  const handleDrop = async (e: React.DragEvent, item: FolderDocumentRequestItemDto) => {
    e.preventDefault();
    setDragOverItemId(null);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(item, files[0]);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, item: FolderDocumentRequestItemDto) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(item, files[0]);
    }
    e.target.value = '';
  };

  const handleCardClick = (itemId: number) => {
    fileInputRefs.current[itemId]?.click();
  };

  const handleFinishUpload = async () => {
    if (!publicId) return;

    setLoading(true);
    try {
      // Mark the request as expired so the client cannot access it again
      await publicFolderUploadApi.completeUploadSession(publicId, accessToken);
    } catch (error) {
      console.error('Error completing upload session:', error);
      // Still show completed screen even if API call fails
    } finally {
      setLoading(false);
      setStep('completed');
    }
  };

  // Handle replace click - opens file picker (NOT preview)
  const handleReplaceClick = (e: React.MouseEvent, itemId: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    // Small delay to ensure the event is fully stopped before opening file picker
    setTimeout(() => {
      handleCardClick(itemId);
    }, 0);
  };

  // Render upload card - cube style
  const renderUploadCard = (item: FolderDocumentRequestItemDto) => {
    const uploadedThisSession = uploadedItems.has(item.id);
    const isUploaded = item.isReceived || uploadedThisSession;
    const isUploading = uploadingItemId === item.id;
    const isDragOver = dragOverItemId === item.id;
    const uploadedFileName = uploadedFileNames[item.id];
    // Document ID can be from current session upload or from backend (previously uploaded)
    const documentId = uploadedDocumentIds[item.id] || item.uploadedDocumentId;
    const canPreview = isUploaded && documentId;

    return (
      <UploadCard
        key={item.id}
        isUploaded={isUploaded}
        isUploading={isUploading}
        isDragOver={isDragOver}
        onClick={() => {
          // Only handle click for non-uploaded files (to open file picker)
          if (isUploading) return;
          if (!isUploaded) {
            handleCardClick(item.id);
          }
          // For uploaded files, clicking is handled by PreviewClickArea and ReplaceText separately
        }}
        onDragOver={(e) => handleDragOver(e, item.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, item)}
      >
        <HiddenInput
          type="file"
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.xls,.xlsx"
          onChange={(e) => handleFileChange(e, item)}
          disabled={isUploading}
          ref={(el) => { fileInputRefs.current[item.id] = el; }}
        />

        {isUploading ? (
          <>
            <CardIcon>⏳</CardIcon>
            <CardTitle>מעלה...</CardTitle>
          </>
        ) : isUploaded && uploadedFileName ? (
          <UploadedFileInfo>
            <PreviewClickArea onClick={(e) => canPreview && handlePreviewClick(item, e)}>
              <CardIcon>{getFileIcon(uploadedFileName)}</CardIcon>
              <FileName title={uploadedFileName}>{uploadedFileName}</FileName>
            </PreviewClickArea>
            <ReplaceText onClick={(e) => handleReplaceClick(e, item.id)}>לחץ להחלפה</ReplaceText>
          </UploadedFileInfo>
        ) : isUploaded ? (
          <UploadedFileInfo>
            <PreviewClickArea onClick={(e) => canPreview && handlePreviewClick(item, e)}>
              {/* File icon based on uploaded file type or default cloud icon */}
              <CardIcon>
                {item.uploadedFileName ? getFileIcon(item.uploadedFileName) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 14C2.89543 13.3333 2 11.8 2 10C2 7.79086 3.79086 6 6 6C6.08236 6 6.16416 6.00209 6.24536 6.00623C7.09645 3.67133 9.36335 2 12 2C14.6366 2 16.9035 3.67133 17.7546 6.00623C17.8358 6.00209 17.9176 6 18 6C20.2091 6 22 7.79086 22 10C22 11.8 21.1046 13.3333 20 14" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M8 15L11 18L16 12" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </CardIcon>
              {item.uploadedFileName && (
                <FileName title={item.uploadedFileName}>{item.uploadedFileName}</FileName>
              )}
              {!item.uploadedFileName && <CardTitle>הועלה בעבר</CardTitle>}
            </PreviewClickArea>
            <ReplaceText onClick={(e) => handleReplaceClick(e, item.id)}>לחץ להחלפה</ReplaceText>
          </UploadedFileInfo>
        ) : (
          <>
            <CardIcon>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 16.2422C2.79401 15.435 2 14.0602 2 12.5C2 10.1564 3.79151 8.23129 6.07974 8.01937C6.54781 5.17213 9.02024 3 12 3C14.9798 3 17.4522 5.17213 17.9203 8.01937C20.2085 8.23129 22 10.1564 22 12.5C22 14.0602 21.206 15.435 20 16.2422" stroke="#012A64" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 21V13M12 13L15 16M12 13L9 16" stroke="#012A64" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CardIcon>
            <CardTitle>{item.targetFolderName}</CardTitle>
            <CardSubtitle>לחץ או גרור קובץ</CardSubtitle>
            {item.isMandatory ? (
              <MandatoryBadge>חובה</MandatoryBadge>
            ) : (
              <OptionalBadge>אופציונלי</OptionalBadge>
            )}
          </>
        )}

        {item.specialInstructions && !isUploaded && (
          <CardSubtitle style={{ marginTop: 8, fontStyle: 'italic' }}>
            {item.specialInstructions}
          </CardSubtitle>
        )}
      </UploadCard>
    );
  };

  // Render access step
  const renderAccessStep = () => (
    <AccessForm>
      <p style={{ color: '#6b7280', marginBottom: 8, fontSize: 14 }}>
        הזן את קוד הגישה שקיבלת במייל
      </p>
      <form onSubmit={handleAccessCodeSubmit}>
        <AccessInput
          type="text"
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
          placeholder="קוד גישה"
          hasError={!!error}
          disabled={loading}
          autoFocus
        />
        {error && <ErrorAlert>{error}</ErrorAlert>}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={loading || accessCode.length < 4}
        >
          {loading ? 'מאמת...' : 'כניסה'}
        </Button>
      </form>
    </AccessForm>
  );

  // Render upload step
  const renderUploadStep = () => (
    <>
      {session && (
        <>
          <Section>
            <SectionTitle>פרטי הבקשה</SectionTitle>
            <InfoBox>
              <strong>לקוח:</strong> {session.clientName}<br />
              <strong>נושא:</strong> {session.requestTitle}
              {session.generalInstructions && (
                <>
                  <br /><br />
                  <strong>הנחיות:</strong><br />
                  {session.generalInstructions}
                </>
              )}
            </InfoBox>
          </Section>

          <Section>
            <SectionTitle>התקדמות</SectionTitle>
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill percent={progressPercent} isComplete={allMandatoryUploaded} />
              </ProgressBar>
              <ProgressText>
                <span>{progressStats.uploaded} מתוך {progressStats.total} הועלו</span>
                {progressStats.mandatoryTotal > 0 && (
                  <span>{progressStats.mandatoryUploaded}/{progressStats.mandatoryTotal} חובה</span>
                )}
              </ProgressText>
            </ProgressContainer>
          </Section>

          {error && <ErrorAlert>{error}</ErrorAlert>}

          <Section>
            <SectionTitle>מסמכים להעלאה</SectionTitle>
            <CardsGrid>
              {session.requestItems.map(item => renderUploadCard(item))}
            </CardsGrid>
          </Section>

          {allMandatoryUploaded && (
            <CompletionBox>
              <CompletionTitle>כל המסמכים הנדרשים הועלו</CompletionTitle>
              <CompletionText>
                ניתן להעלות מסמכים נוספים או לסיים את התהליך
              </CompletionText>
              <Button variant="success" onClick={handleFinishUpload} disabled={loading}>
                {loading ? 'שולח...' : 'סיום'}
              </Button>
            </CompletionBox>
          )}
        </>
      )}
    </>
  );

  // Render completed step
  const renderCompletedStep = () => (
    <CompletedView>
      <CompletedIcon>✓</CompletedIcon>
      <CompletedTitle>תודה רבה</CompletedTitle>
      <CompletedText>
        המסמכים הועלו בהצלחה.<br />
        צוות המשרד יבדוק אותם בהקדם.
      </CompletedText>
    </CompletedView>
  );

  // Render expired step
  const renderExpiredStep = () => (
    <ExpiredView>
      <ExpiredIcon>⏰</ExpiredIcon>
      <ExpiredTitle>פג תוקף הקישור</ExpiredTitle>
      <ExpiredText>
        הקישור להעלאת המסמכים כבר אינו פעיל.<br /><br />
        אנא פנה למשרד לקבלת קישור חדש.
      </ExpiredText>
    </ExpiredView>
  );

  // Render preview content
  const renderPreviewContent = () => {
    if (!previewItem) return null;

    const fileExtension = previewItem.uploadedFileExtension || '';
    const previewInfo = isPreviewableFile(fileExtension);

    if (isLoadingPreview) {
      return (
        <PreviewLoading>
          <span style={{ fontSize: '40px' }}>⏳</span>
          <span>טוען תצוגה מקדימה...</span>
        </PreviewLoading>
      );
    }

    if (!previewInfo.canPreview) {
      return (
        <UnsupportedPreview>
          <div style={{ fontSize: '72px', marginBottom: '20px' }}>
            {previewItem.uploadedFileName ? getFileIcon(previewItem.uploadedFileName) : '📄'}
          </div>
          <UnsupportedTitle>לא ניתן להציג תצוגה מקדימה</UnsupportedTitle>
          <UnsupportedText>
            קבצי {getFileTypeName(fileExtension)} אינם נתמכים לתצוגה מקדימה.
          </UnsupportedText>
          <DownloadButton onClick={handleDownload} disabled={isDownloading}>
            {isDownloading ? (
              <>⏳ מוריד...</>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                הורד קובץ
              </>
            )}
          </DownloadButton>
        </UnsupportedPreview>
      );
    }

    if (!previewUrl) {
      return (
        <PreviewLoading>
          <span>שגיאה בטעינת התצוגה המקדימה</span>
        </PreviewLoading>
      );
    }

    if (previewInfo.type === 'pdf') {
      return <PreviewIframe src={previewUrl} title={previewItem.uploadedFileName || 'Preview'} />;
    }

    if (previewInfo.type === 'image') {
      return <PreviewImage src={previewUrl} alt={previewItem.uploadedFileName || 'Preview'} />;
    }

    return null;
  };

  return (
    <PageContainer>
      <MainCard>
        <Header>
          <LogoContainer>
            <UploadLogo src="/logomagialecha.png" alt="לוגו מגיע לך" />
          </LogoContainer>
          <Title>העלאת מסמכים</Title>
          <Subtitle>מערכת מאובטחת להעלאת מסמכים</Subtitle>
        </Header>

        <Content>
          {step !== 'expired' && (
            <StepIndicator>
              <Step $completed={step !== 'access'} $active={step === 'access'}>
                <StepCircle $completed={step !== 'access'} $active={step === 'access'}>
                  {step !== 'access' ? '✓' : '1'}
                </StepCircle>
                <StepLabel $active={step === 'access'}>אימות</StepLabel>
              </Step>
              <Step $completed={step === 'completed'} $active={step === 'upload'}>
                <StepCircle $completed={step === 'completed'} $active={step === 'upload'}>
                  {step === 'completed' ? '✓' : '2'}
                </StepCircle>
                <StepLabel $active={step === 'upload'}>העלאה</StepLabel>
              </Step>
              <Step $active={step === 'completed'}>
                <StepCircle $active={step === 'completed'}>
                  {step === 'completed' ? '✓' : '3'}
                </StepCircle>
                <StepLabel $active={step === 'completed'}>סיום</StepLabel>
              </Step>
            </StepIndicator>
          )}

          {step === 'access' && renderAccessStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'completed' && renderCompletedStep()}
          {step === 'expired' && renderExpiredStep()}
        </Content>
      </MainCard>

      {/* Preview Modal */}
      {previewItem && (
        <ModalOverlay onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {previewItem.uploadedFileName ? getFileIcon(previewItem.uploadedFileName) : '📄'} {previewItem.uploadedFileName || previewItem.targetFolderName}
              </ModalTitle>
              <CloseButton onClick={handleClosePreview} title="סגור">
                ✕
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              {renderPreviewContent()}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default PublicFolderUploadPage;
